import './jwt-debugger.css';
import { CheckCircledIcon, CrossCircledIcon, ExclamationTriangleIcon, InfoCircledIcon, LockClosedIcon } from '@radix-ui/react-icons';
import { Box, Button, Callout, Card, CopyButton, DataList, Flex, Heading, SegmentedControl, Separator, Text, TextField, VisuallyHidden } from '@workos-inc/design-system';
import * as React from 'react';
import { EXAMPLE_JWT, EXAMPLE_SECRET } from './example-token';
import { formatClaimTitle, TIMESTAMP_CLAIMS } from './jwt-claims';
import { type DecodedJwt, decodeJwt, getAlgorithmInfo, verifySignature } from './jwt-utils';

/** Syntax-highlight a JSON string with span-based coloring. */
function highlightJson(obj: Record<string, unknown>): React.ReactNode[] {
  const json = JSON.stringify(obj, null, 2);
  const nodes: React.ReactNode[] = [];
  const tokenRegex = /("(?:[^"\\]|\\.)*")\s*:|("(?:[^"\\]|\\.)*")|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|(\btrue\b|\bfalse\b)|(\bnull\b)|([{}[\]:,])/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null = tokenRegex.exec(json);
  while (match) {
    if (match.index > lastIndex) {
      nodes.push(json.slice(lastIndex, match.index));
    }
    if (match[1]) {
      nodes.push(<span key={`k${match.index}`} className="JwtJsonKey">
          {match[1]}
        </span>);
      nodes.push(':');
    } else if (match[2]) {
      nodes.push(<span key={`s${match.index}`} className="JwtJsonString">
          {match[2]}
        </span>);
    } else if (match[3]) {
      nodes.push(<span key={`n${match.index}`} className="JwtJsonNumber">
          {match[3]}
        </span>);
    } else if (match[4]) {
      nodes.push(<span key={`b${match.index}`} className="JwtJsonBool">
          {match[4]}
        </span>);
    } else if (match[5]) {
      nodes.push(<span key={`u${match.index}`} className="JwtJsonNull">
          {match[5]}
        </span>);
    } else if (match[6]) {
      nodes.push(<span key={`p${match.index}`} className="JwtJsonPunctuation">
          {match[6]}
        </span>);
    }
    lastIndex = match.index + match[0].length;
    match = tokenRegex.exec(json);
  }
  if (lastIndex < json.length) {
    nodes.push(json.slice(lastIndex));
  }
  return nodes;
}

/** Format a field value for display in the parsed (key/value) view. */
function formatClaimValue(key: string, value: unknown): string {
  if (TIMESTAMP_CLAIMS.has(key) && typeof value === 'number') {
    try {
      const date = new Date(value * 1000);
      return `${String(value)} (${date.toUTCString()})`;
    } catch {
      return String(value);
    }
  }
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  return String(value);
}

/** Parsed JSON object as DataList rows (human label + raw key + value). */
function ClaimsDataList({
  data
}: {
  data: Record<string, unknown>;
}) {
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return <div className="JwtEmptyState">No fields</div>;
  }
  return <div className="JwtClaimsDataList" data-sentry-component="ClaimsDataList" data-sentry-source-file="jwt-debugger.tsx">
      <DataList.Root orientation="horizontal" size="2" data-sentry-element="DataList.Root" data-sentry-source-file="jwt-debugger.tsx">
        {entries.map(([key, value]) => {
        return <DataList.Item key={key} align="start">
              <DataList.Label>
                <Flex align="start" direction="column" gap="0">
                  <Text size="2" weight="bold">
                    {formatClaimTitle(key)}
                  </Text>
                  <Text color="gray" size="1" style={{
                fontFamily: 'var(--code-font-family)'
              }}>
                    {key}
                  </Text>
                </Flex>
              </DataList.Label>
              <DataList.Value>
                <Text className="JwtClaimsDataListValue" size="2">
                  {formatClaimValue(key, value)}
                </Text>
              </DataList.Value>
            </DataList.Item>;
      })}
      </DataList.Root>
    </div>;
}
type DecodedViewMode = 'json' | 'parsed';

/** One decoded part (header or payload); view mode controlled by parent. */
function DecodedSection({
  label,
  data,
  viewMode
}: {
  label: string;
  data: Record<string, unknown>;
  viewMode: DecodedViewMode;
}) {
  const formatted = JSON.stringify(data, null, 2);
  return <div className="JwtDecodedSection" data-sentry-component="DecodedSection" data-sentry-source-file="jwt-debugger.tsx">
      <div className="JwtDecodedSectionToolbar">
        <Text color="gray" size="1" weight="bold" data-sentry-element="Text" data-sentry-source-file="jwt-debugger.tsx">
          {label}
        </Text>
        <CopyButton onClick={() => {
        void navigator.clipboard.writeText(formatted);
      }} data-sentry-element="CopyButton" data-sentry-source-file="jwt-debugger.tsx" />
      </div>
      {viewMode === 'json' ? <pre className="JwtJsonOutput">{highlightJson(data)}</pre> : <ClaimsDataList data={data} />}
    </div>;
}

/** Decoded column: success card, empty state, or decode error. */
function JwtDecodedPanel({
  decoded,
  token,
  viewMode,
  onViewModeChange
}: {
  decoded: DecodedJwt | null;
  token: string;
  viewMode: DecodedViewMode;
  onViewModeChange: (mode: DecodedViewMode) => void;
}) {
  const decodedViewFormatLabelId = React.useId();
  if (decoded) {
    return <Card className="JwtDecodedCard" size="2">
        <Box pb="4">
          <Flex align="start" justify="between" gap="3" wrap="wrap">
            <Flex direction="column" gap="2">
              <Text size="4" style={{
              color: 'var(--gray-12)'
            }} weight="bold">
                Decoded token
              </Text>
              <Text size="2" style={{
              color: 'var(--gray-12)'
            }}>
                Header and payload extracted from your JWT
              </Text>
            </Flex>
            <VisuallyHidden>
              <span id={decodedViewFormatLabelId}>
                Format for header and payload sections: raw JSON or parsed
                fields with labels
              </span>
            </VisuallyHidden>
            <SegmentedControl.Root aria-labelledby={decodedViewFormatLabelId} size="2" value={viewMode} onValueChange={value => {
            if (value === 'json' || value === 'parsed') {
              onViewModeChange(value);
            }
          }}>
              <SegmentedControl.Item aria-label="Raw JSON" value="json">
                JSON
              </SegmentedControl.Item>
              <SegmentedControl.Item aria-label="Parsed fields with human-readable labels" value="parsed">
                Parsed
              </SegmentedControl.Item>
            </SegmentedControl.Root>
          </Flex>
        </Box>
        <Flex className="JwtDecodedOutput" direction="column" gap="3">
          <DecodedSection data={decoded.header} label="HEADER" viewMode={viewMode} />
          <DecodedSection data={decoded.payload} label="PAYLOAD" viewMode={viewMode} />
        </Flex>
      </Card>;
  }
  if (!token.trim()) {
    return <div className="JwtDecodedEmptyPanelShell">
        <div className="JwtDecodedEmptyPanel" role="status">
          <LockClosedIcon aria-hidden className="JwtDecodedEmptyPanelIcon" height={56} width={56} />
          <Text className="JwtDecodedEmptyPanelTitle" size="4" weight="bold">
            No decoded token yet
          </Text>
          <Text className="JwtDecodedEmptyPanelHint" color="gray" size="2">
            Paste an encoded JWT on the left to see the header and payload here.
          </Text>
        </div>
      </div>;
  }
  return <div className="JwtDecodedSection JwtDecodedErrorPanel" data-sentry-component="JwtDecodedPanel" data-sentry-source-file="jwt-debugger.tsx">
      <div className="JwtEmptyState">Unable to decode token</div>
    </div>;
}

/**
 * Build colored segment spans for a decoded JWT and append them to a
 * container element. Uses safe DOM methods (createElement / textContent)
 * to avoid innerHTML and XSS risk.
 */
function setSegmentContent(container: HTMLElement, parts: [string, string, string]): void {
  container.textContent = '';
  const segments: [string, string][] = [['JwtSegmentHeader', parts[0]], ['JwtSegmentDot', '.'], ['JwtSegmentPayload', parts[1]], ['JwtSegmentDot', '.'], ['JwtSegmentSignature', parts[2]]];
  for (const [cls, text] of segments) {
    const span = document.createElement('span');
    span.className = cls;
    span.textContent = text;
    container.appendChild(span);
  }
}
export const JwtDebugger = () => {
  const [token, setToken] = React.useState(EXAMPLE_JWT);
  const [decoded, setDecoded] = React.useState<DecodedJwt | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [secret, setSecret] = React.useState(EXAMPLE_SECRET);
  const [publicKey, setPublicKey] = React.useState('');
  const [signatureValid, setSignatureValid] = React.useState<boolean | null>(null);
  const [verifying, setVerifying] = React.useState(false);
  const [decodedViewMode, setDecodedViewMode] = React.useState<DecodedViewMode>('parsed');
  const editableRef = React.useRef<HTMLDivElement>(null);
  const justPasted = React.useRef(false);
  const encodedTokenLabelId = React.useId();
  const encodedTokenDescriptionId = React.useId();
  const algInfo = decoded ? getAlgorithmInfo(decoded.header.alg) : null;
  const verifyInput = algInfo?.family === 'hmac' ? secret : publicKey;
  React.useEffect(() => {
    setSignatureValid(null);
    if (!token.trim()) {
      setDecoded(null);
      setError(null);
      return;
    }
    try {
      const result = decodeJwt(token);
      setDecoded(result);
      setError(null);
    } catch (e) {
      setDecoded(null);
      setError(e instanceof Error ? e.message : 'Failed to decode token');
    }
  }, [token]);

  // Imperatively update the contentEditable display to avoid React
  // reconciliation conflicts with browser-mutated DOM nodes.
  // Skip the update while the user is actively typing (element focused)
  // to prevent cursor-position resets. Paste sets a flag to bypass
  // the focus check since the content is fully replaced.
  React.useEffect(() => {
    const el = editableRef.current;
    if (!el) {
      return;
    }
    const wasPaste = justPasted.current;
    justPasted.current = false;
    if (document.activeElement === el && !wasPaste) {
      return;
    }
    if (decoded) {
      setSegmentContent(el, decoded.parts);
    } else if (token) {
      el.textContent = token;
    } else {
      el.textContent = '';
    }
  }, [decoded, token]);
  const handleEditableBlur = () => {
    const el = editableRef.current;
    if (!el) {
      return;
    }
    if (decoded) {
      setSegmentContent(el, decoded.parts);
    } else if (token) {
      el.textContent = token;
    }
  };
  const handleVerify = async () => {
    if (!algInfo || algInfo.family === 'unsupported') {
      return;
    }
    if (!token.trim() || !verifyInput.trim()) {
      return;
    }
    setVerifying(true);
    try {
      const isValid = await verifySignature(token, verifyInput, algInfo);
      setSignatureValid(isValid);
    } finally {
      setVerifying(false);
    }
  };
  return <Box data-sentry-element="Box" data-sentry-component="JwtDebugger" data-sentry-source-file="jwt-debugger.tsx">
      <Heading as="h1" mb="2" size={{
      initial: '7',
      sm: '8'
    }} data-sentry-element="Heading" data-sentry-source-file="jwt-debugger.tsx">
        JWT Debugger
      </Heading>
      <Text as="p" color="gray" mb="6" size="3" data-sentry-element="Text" data-sentry-source-file="jwt-debugger.tsx">
        Paste a JWT to instantly decode its header and payload and optionally
        verify signatures with HMAC, RSA, ECDSA, or RSA-PSS keys, all processed
        locally in your browser.
      </Text>

      <Callout.Root color="gray" mb="5" data-sentry-element="Callout.Root" data-sentry-source-file="jwt-debugger.tsx">
        <Callout.Icon data-sentry-element="Callout.Icon" data-sentry-source-file="jwt-debugger.tsx">
          <InfoCircledIcon height="16" width="16" data-sentry-element="InfoCircledIcon" data-sentry-source-file="jwt-debugger.tsx" />
        </Callout.Icon>
        <Text as="div" className="rt-CalloutText" size="2" data-sentry-element="Text" data-sentry-source-file="jwt-debugger.tsx">
          All decoding and verification happens in your browser. Tokens are
          never sent to a server. Be careful where you paste JWTs as they may
          grant access to resources.
        </Text>
      </Callout.Root>

      {error && <Callout.Root color="red" mb="5">
          <Callout.Icon>
            <ExclamationTriangleIcon height="16" width="16" />
          </Callout.Icon>
          <Text as="div" className="rt-CalloutText" size="2">
            {error}
          </Text>
        </Callout.Root>}

      <div className="JwtDebuggerGrid" data-empty-encoded={!token.trim() ? true : undefined}>
        {/* Left panel — Encoded token with inline color highlighting */}
        <Box className="JwtPanel" data-sentry-element="Box" data-sentry-source-file="jwt-debugger.tsx">
          <Card className="JwtEncodedCard" size="2" data-sentry-element="Card" data-sentry-source-file="jwt-debugger.tsx">
            <Box pb="4" data-sentry-element="Box" data-sentry-source-file="jwt-debugger.tsx">
              <Flex align="start" justify="between" gap="3" data-sentry-element="Flex" data-sentry-source-file="jwt-debugger.tsx">
                <Flex direction="column" gap="2" data-sentry-element="Flex" data-sentry-source-file="jwt-debugger.tsx">
                  <Text id={encodedTokenLabelId} size="4" style={{
                  color: 'var(--gray-12)'
                }} weight="bold" data-sentry-element="Text" data-sentry-source-file="jwt-debugger.tsx">
                    Encoded token
                  </Text>
                  <Text id={encodedTokenDescriptionId} size="2" style={{
                  color: 'var(--gray-12)'
                }} data-sentry-element="Text" data-sentry-source-file="jwt-debugger.tsx">
                    Paste your JWT and optionally provide a key to verify
                  </Text>
                </Flex>
                {token.trim() ? <CopyButton onClick={() => {
                void navigator.clipboard.writeText(token.trim());
              }} /> : null}
              </Flex>
            </Box>
            <div className="JwtEncodedEditorShell">
              <div ref={editableRef} role="textbox" aria-multiline="true" aria-labelledby={encodedTokenLabelId} aria-describedby={encodedTokenDescriptionId} aria-placeholder="Paste a JWT here..." contentEditable suppressContentEditableWarning className="JwtTokenEditable" data-placeholder="Paste a JWT here..." spellCheck={false} onBlur={handleEditableBlur} onInput={e => {
              setToken(e.currentTarget.textContent ?? '');
            }} onPaste={e => {
              e.preventDefault();
              const text = e.clipboardData.getData('text/plain').trim();
              justPasted.current = true;
              setToken(text);
            }} />
            </div>
            <Box className="JwtLegend" pt="3" data-sentry-element="Box" data-sentry-source-file="jwt-debugger.tsx">
              <div className="JwtLegendItem">
                <span className="JwtLegendDot" data-segment="header" />
                <Text color="gray" size="1" data-sentry-element="Text" data-sentry-source-file="jwt-debugger.tsx">
                  Header
                </Text>
              </div>
              <div className="JwtLegendItem">
                <span className="JwtLegendDot" data-segment="payload" />
                <Text color="gray" size="1" data-sentry-element="Text" data-sentry-source-file="jwt-debugger.tsx">
                  Payload
                </Text>
              </div>
              <div className="JwtLegendItem">
                <span className="JwtLegendDot" data-segment="signature" />
                <Text color="gray" size="1" data-sentry-element="Text" data-sentry-source-file="jwt-debugger.tsx">
                  Signature
                </Text>
              </div>
            </Box>
          </Card>
        </Box>

        {/* Right panel — Decoded header + payload stacked */}
        <Box className="JwtPanel JwtPanel--decoded" data-sentry-element="Box" data-sentry-source-file="jwt-debugger.tsx">
          <JwtDecodedPanel decoded={decoded} token={token} viewMode={decodedViewMode} onViewModeChange={setDecodedViewMode} data-sentry-element="JwtDecodedPanel" data-sentry-source-file="jwt-debugger.tsx" />
        </Box>
      </div>

      <Separator my="5" size="4" data-sentry-element="Separator" data-sentry-source-file="jwt-debugger.tsx" />

      {/* Signature verification */}
      <div className="JwtVerifySection">
        <div className="JwtVerifyHeader">
          <LockClosedIcon height="14" width="14" data-sentry-element="LockClosedIcon" data-sentry-source-file="jwt-debugger.tsx" />
          <Text size="2" weight="bold" data-sentry-element="Text" data-sentry-source-file="jwt-debugger.tsx">
            Verify Signature
          </Text>
          {algInfo && <Text color="gray" size="1">
              {algInfo.label}
            </Text>}
        </div>
        <div className="JwtVerifyBody">
          {(() => {
          if (!algInfo || algInfo.family === 'unsupported') {
            return <Text as="p" color="gray" size="2">
                  {algInfo ? `Verification is not supported for ${algInfo.label} tokens.` : 'Paste a token to verify its signature.'}
                </Text>;
          }
          if (algInfo.family === 'hmac') {
            return <Flex align="end" gap="3">
                  <Box flexGrow="1">
                    <Text as="label" color="gray" mb="1" size="1">
                      Secret
                    </Text>
                    <TextField.Root placeholder="Enter HMAC secret..." value={secret} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSecret(e.target.value);
                  setSignatureValid(null);
                }} />
                  </Box>
                  <Button color="purple" disabled={!token.trim() || !verifyInput.trim() || verifying} loading={verifying} onClick={() => {
                void handleVerify();
              }}>
                    Verify
                  </Button>
                </Flex>;
          }
          return <Flex direction="column" gap="3">
                <Box>
                  <Text as="label" color="gray" mb="1" size="1">
                    Public Key (PEM or JWK)
                  </Text>
                  <textarea className="JwtPublicKeyTextarea" rows={4} spellCheck={false} value={publicKey} placeholder={'-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----'} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                setPublicKey(e.target.value);
                setSignatureValid(null);
              }} />
                </Box>
                <Box>
                  <Button color="purple" disabled={!token.trim() || !verifyInput.trim() || verifying} loading={verifying} onClick={() => {
                void handleVerify();
              }}>
                    Verify
                  </Button>
                </Box>
              </Flex>;
        })()}

          {signatureValid !== null && <Box mt="3">
              <div className="JwtVerifyResult" data-status={signatureValid ? 'valid' : 'invalid'}>
                {signatureValid ? <CheckCircledIcon className="JwtVerifyIcon" /> : <CrossCircledIcon className="JwtVerifyIcon" />}
                {signatureValid ? 'Signature is valid' : 'Signature is invalid'}
              </div>
            </Box>}

          {typeof crypto !== 'undefined' && !crypto?.subtle && <Text as="p" color="red" mt="2" size="1">
              Signature verification requires a secure context (HTTPS).
            </Text>}
        </div>
      </div>
    </Box>;
};